// Stage 5.1 (2026-05-25) — Car Ownership Detail panel.
//
// Mirrors PropertyTaxDetail structure and visual language (Quiet Atlas).
// Used in: NextStepsPage "Getting around" section + CountryScoreCard tooltip.

import type { CarOwnershipEntry, CarOwnershipTier } from './data';
import {
  formatCarTierLabel,
  getCarTierColor,
  formatCarHeadline,
} from './data';

export interface CarOwnershipDetailProps {
  entry: CarOwnershipEntry;
  countryCode: string;
  lang?: 'en' | 'ru';
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <dt className="text-[11px] font-medium uppercase tracking-widest text-ink-400">
      {children}
    </dt>
  );
}

function SectionValue({ children }: { children: React.ReactNode }) {
  return (
    <dd className="mt-0.5 text-sm text-ink-900">{children}</dd>
  );
}

function DataRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-2 py-1.5 border-b border-ink-100 last:border-0">
      <span className="text-xs text-ink-500 shrink-0">{label}</span>
      <span className="text-xs text-ink-800 text-right">{value}</span>
    </div>
  );
}

const TIER_ICON: Record<CarOwnershipTier, string> = {
  very_low: '🟢',
  low: '🟡',
  medium: '🟠',
  high: '🔴',
  very_high: '🔴',
};

export function CarOwnershipDetail({ entry, countryCode: _countryCode, lang = 'en' }: CarOwnershipDetailProps) {
  const isLowConf = entry.confidence === 'low';
  const approx = entry.confidence === 'medium' ? '~' : '';
  const tierLabel = formatCarTierLabel(entry.tier, lang);
  const tierColor = getCarTierColor(entry.tier);
  const headline = formatCarHeadline(entry, lang);

  return (
    <div className="space-y-4 text-ink-900">
      {/* Tier badge + headline */}
      <div className="flex flex-col gap-1.5">
        <span className={`inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1 text-xs font-medium ${tierColor}`}>
          {TIER_ICON[entry.tier]} {tierLabel}
        </span>
        {headline && (
          <p className="text-sm text-ink-600">{headline}</p>
        )}
      </div>

      {isLowConf ? (
        <p className="text-sm text-ink-400 italic">
          {lang === 'ru'
            ? 'Детальные данные по этой стране уточняются. Ниже — общие тезисы.'
            : 'Detailed data for this country is still being verified. General notes below.'}
        </p>
      ) : (
        <>
          {/* Monthly cost summary */}
          {entry.monthly_total_est_eur != null && (
            <div className="rounded-lg bg-ink-50 px-4 py-3">
              <p className="text-xs text-ink-500">
                {lang === 'ru' ? 'Примерные ежемесячные расходы' : 'Estimated monthly total'}
              </p>
              <p className="text-2xl font-semibold text-ink-900">
                {approx}€{entry.monthly_total_est_eur}
                <span className="text-sm font-normal text-ink-400">
                  {lang === 'ru' ? '/мес' : '/mo'}
                </span>
              </p>
              <p className="mt-0.5 text-[11px] text-ink-400">
                {lang === 'ru'
                  ? 'Владение + ~1 000 км топлива в месяц'
                  : 'Ownership + ~1,000 km of fuel per month'}
              </p>
            </div>
          )}

          {/* Purchase costs */}
          {(entry.purchase.vat_pct != null || entry.purchase.registration_tax_pct != null) && (
            <dl className="space-y-0">
              <SectionLabel>{lang === 'ru' ? 'Расходы при покупке' : 'Purchase costs'}</SectionLabel>
              <SectionValue>
                <div className="mt-1 divide-y divide-ink-100 rounded-md border border-ink-100 overflow-hidden">
                  {entry.purchase.vat_pct != null && (
                    <DataRow
                      label={lang === 'ru' ? 'НДС на новый авт.' : 'VAT on new car'}
                      value={`${entry.purchase.vat_pct}%`}
                    />
                  )}
                  {entry.purchase.registration_tax_pct != null && (
                    <DataRow
                      label={lang === 'ru' ? 'Налог при регистрации' : 'Registration tax'}
                      value={`${entry.purchase.registration_tax_pct}%`}
                    />
                  )}
                  {entry.purchase.import_duty_pct != null && entry.purchase.import_duty_pct > 0 && (
                    <DataRow
                      label={lang === 'ru' ? 'Ввозная пошлина' : 'Import duty'}
                      value={`${entry.purchase.import_duty_pct}%`}
                    />
                  )}
                </div>
              </SectionValue>
              {(entry.purchase.notes_en || entry.purchase.notes_ru) && (
                <p className="mt-1 text-xs text-ink-500">
                  {lang === 'ru' ? (entry.purchase.notes_ru ?? entry.purchase.notes_en) : entry.purchase.notes_en}
                </p>
              )}
            </dl>
          )}

          {/* Annual costs */}
          {entry.annual.total_annual_est_eur != null && (
            <dl>
              <SectionLabel>{lang === 'ru' ? 'Ежегодные расходы' : 'Annual ownership costs'}</SectionLabel>
              <SectionValue>
                <div className="mt-1 divide-y divide-ink-100 rounded-md border border-ink-100 overflow-hidden">
                  {entry.annual.road_tax_est_eur != null && (
                    <DataRow
                      label={lang === 'ru' ? 'Транспортный налог' : 'Road tax'}
                      value={`${approx}€${entry.annual.road_tax_est_eur}`}
                    />
                  )}
                  {entry.annual.insurance_3rd_party_est_eur != null && (
                    <DataRow
                      label={lang === 'ru' ? 'ОСАГО (3rd party)' : '3rd party insurance'}
                      value={`${approx}€${entry.annual.insurance_3rd_party_est_eur}`}
                    />
                  )}
                  {entry.annual.inspection_years != null && (
                    <DataRow
                      label={lang === 'ru' ? 'Техосмотр' : 'Vehicle inspection'}
                      value={`€${entry.annual.inspection_cost_eur ?? '?'} / ${entry.annual.inspection_years} ${lang === 'ru' ? 'лет' : 'yr'}`}
                    />
                  )}
                  <DataRow
                    label={lang === 'ru' ? 'Итого/год' : 'Total/year'}
                    value={`${approx}€${entry.annual.total_annual_est_eur}`}
                  />
                </div>
              </SectionValue>
            </dl>
          )}

          {/* Fuel prices */}
          {(entry.fuel.gasoline_eur_litre != null || entry.fuel.diesel_eur_litre != null) && (
            <dl>
              <SectionLabel>
                {lang === 'ru' ? 'Топливо' : 'Fuel prices'}
                {entry.fuel.reference_date && (
                  <span className="ml-1 font-normal normal-case text-ink-300">
                    ({entry.fuel.reference_date})
                  </span>
                )}
              </SectionLabel>
              <SectionValue>
                <div className="mt-1 flex gap-3">
                  {entry.fuel.gasoline_eur_litre != null && (
                    <span className="rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
                      ⛽ €{entry.fuel.gasoline_eur_litre.toFixed(2)}/L
                    </span>
                  )}
                  {entry.fuel.diesel_eur_litre != null && (
                    <span className="rounded bg-sky-50 px-2 py-1 text-xs font-medium text-sky-800">
                      🚛 €{entry.fuel.diesel_eur_litre.toFixed(2)}/L diesel
                    </span>
                  )}
                </div>
              </SectionValue>
            </dl>
          )}

          {/* Used car market */}
          {entry.market.used_compact_5yr_eur != null && (
            <dl>
              <SectionLabel>{lang === 'ru' ? 'Рынок б/у автомобилей' : 'Used car market'}</SectionLabel>
              <SectionValue>
                <div className="mt-1 divide-y divide-ink-100 rounded-md border border-ink-100 overflow-hidden">
                  <DataRow
                    label={lang === 'ru' ? 'Компакт 5 лет (Golf/Corolla)' : '5yr compact (Golf/Corolla)'}
                    value={`${approx}€${(entry.market.used_compact_5yr_eur / 1000).toFixed(0)}k`}
                  />
                  {entry.market.new_compact_eur != null && (
                    <DataRow
                      label={lang === 'ru' ? 'Новый эквивалент' : 'New equivalent'}
                      value={`${approx}€${(entry.market.new_compact_eur / 1000).toFixed(0)}k`}
                    />
                  )}
                  {entry.market.market_quality && (
                    <DataRow
                      label={lang === 'ru' ? 'Качество рынка' : 'Market quality'}
                      value={
                        entry.market.market_quality === 'good'
                          ? (lang === 'ru' ? '✅ Хороший' : '✅ Good')
                          : entry.market.market_quality === 'fair'
                            ? (lang === 'ru' ? '🟡 Средний' : '🟡 Fair')
                            : (lang === 'ru' ? '⚠ Слабый' : '⚠ Poor')
                      }
                    />
                  )}
                </div>
              </SectionValue>
              {entry.market.notes_en && (
                <p className="mt-1 text-xs text-ink-500">
                  {lang === 'ru' ? (entry.market.notes_ru ?? entry.market.notes_en) : entry.market.notes_en}
                </p>
              )}
            </dl>
          )}

          {/* Driving licence for expats */}
          {entry.license && (
            <dl>
              <SectionLabel>{lang === 'ru' ? 'Водительское удостоверение' : 'Driving licence (expats)'}</SectionLabel>
              <SectionValue>
                <p className="mt-1 text-sm text-ink-700">
                  {lang === 'ru'
                    ? (entry.license.notes_ru ?? entry.license.notes_en ?? '')
                    : (entry.license.notes_en ?? '')}
                </p>
              </SectionValue>
            </dl>
          )}

          {/* Gotchas */}
          {((lang === 'ru' ? entry.gotchas_ru : entry.gotchas_en)?.length ?? 0) > 0 && (
            <dl>
              <SectionLabel>{lang === 'ru' ? 'Важно знать' : 'Key gotchas'}</SectionLabel>
              <SectionValue>
                <ul className="mt-1 space-y-1">
                  {(lang === 'ru' ? entry.gotchas_ru : entry.gotchas_en)?.map((g, i) => (
                    <li key={i} className="flex gap-2 text-xs text-ink-700">
                      <span className="shrink-0 text-amber-500">⚠</span>
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </SectionValue>
            </dl>
          )}
        </>
      )}

      {/* Notes */}
      <p className="text-xs text-ink-500 leading-relaxed border-t border-ink-100 pt-3">
        {lang === 'ru' ? entry.notes_ru : entry.notes_en}
      </p>

      <p className="text-[10px] text-ink-300">
        {lang === 'ru'
          ? `Данные актуальны на ${entry.last_updated}. Не является финансовой консультацией.`
          : `Data as of ${entry.last_updated}. Not financial advice.`}
      </p>
    </div>
  );
}
